export const CARD_SVG = {
    width: 287,
    height: 210,
};

export const CARD_TAB = {
    startX: 158,
    bottomY: 74,
    centerX: 201,
    centerY: 35,
};

export const CARD_BODY = {
    insetLeftX: 22,
    insetBottomY: 26,
    textMaxRightX: 154,
};

export const TITLE_REGION = {
    topY: 22,
    bottomY: 64,
};

export const BODY_REGION = {
    topY: 72,
};

export const BADGE_SVG = {
    width: 97,
    height: 119,
    circleCenterX: 59.082,
    circleCenterY: 56.4766,
    circleSize: 40,
};

/** Slightly below artboard width so cards don't feel oversized on desktop. */
export const MAX_CARD_WIDTH = 275;
export const XL_INLINE_CARD_WIDTH = 275;
/** Badge circle in card SVG coords — between asset (40) and previous tuning (60). */
export const BADGE_DISPLAY_CIRCLE_SIZE = 50;
export const XL_MIN_INLINE_CARD_WIDTH = 235;
export const MIN_INLINE_CARD_WIDTH = 132;
export const MAX_CARD_HEIGHT_MULTIPLIER = 2;
export const INLINE_CARD_GAP = 10;
export const MD_INLINE_CARD_GAP = 12;
export const LG_INLINE_CARD_GAP = 16;
export const MOBILE_SCROLL_CARD_WIDTH_RATIO = 0.78;
export const MOBILE_MAX_CARD_WIDTH = 268;
export const TABLET_SCROLL_MAX_CARD_WIDTH = 240;
export const MD_LG_SCROLL_MAX_CARD_WIDTH = 248;
export const SCROLL_PEEK_VISIBLE_CARDS_MD = 2.15;
export const SCROLL_PEEK_VISIBLE_CARDS_MOBILE = 1.22;
export const SCROLL_PEEK_VISIBLE_CARDS_TABLET = 1.65;
export const SCROLL_PEEK_VISIBLE_CARDS_MD_LG = 1.55;

export const getScrollCardWidthForPeek = (
    containerWidth,
    visibleCards = SCROLL_PEEK_VISIBLE_CARDS_MD,
    gap = INLINE_CARD_GAP,
    maxWidth = MOBILE_MAX_CARD_WIDTH,
) => {
    if (!containerWidth || visibleCards <= 0) {
        return maxWidth;
    }

    const gapCount = Math.max(Math.ceil(visibleCards) - 1, 0);

    return Math.min(
        Math.floor((containerWidth - gap * gapCount) / visibleCards),
        maxWidth,
    );
};

export const getCardDimensions = (cardWidth, maxWidth = MAX_CARD_WIDTH, heightOverride = null) => {
    const width = Math.min(Math.round(cardWidth), maxWidth);
    const height = heightOverride != null
        ? Math.round(heightOverride)
        : Math.round(width * (CARD_SVG.height / CARD_SVG.width));

    return { width, height };
};

export const getMaxCardHeight = (cardWidth, maxWidth = MAX_CARD_WIDTH) => {
    const { height: baseHeight } = getCardDimensions(cardWidth, maxWidth);
    return Math.ceil(baseHeight * MAX_CARD_HEIGHT_MULTIPLIER);
};

export const getCardHeightForBodyContent = (
    bodyContentHeight,
    cardWidth,
    maxWidth = MAX_CARD_WIDTH,
    { bodyTopMargin = 10, safetyPad = 16 } = {},
) => {
    const { height: referenceHeight } = getCardDimensions(cardWidth, maxWidth);

    if (!bodyContentHeight || bodyContentHeight <= 0) {
        return referenceHeight;
    }

    const bodyTop = sy(BODY_REGION.topY, referenceHeight);
    const bodyBottomInset = sy(CARD_BODY.insetBottomY, referenceHeight);
    const baseBodyInner = referenceHeight - bodyTop - bodyBottomInset - bodyTopMargin;

    if (bodyContentHeight <= baseBodyInner) {
        return referenceHeight;
    }

    const extraBodyHeight = bodyContentHeight + safetyPad - baseBodyInner;

    return Math.ceil(referenceHeight + extraBodyHeight);
};

export const getUniformCardHeightOverride = (
    bodyContentHeights,
    cardWidth,
    maxWidth = MAX_CARD_WIDTH,
    options = {},
) => {
    if (!bodyContentHeights?.length) {
        return null;
    }

    const maxBodyHeight = Math.max(...bodyContentHeights);

    if (!Number.isFinite(maxBodyHeight) || maxBodyHeight <= 0) {
        return null;
    }

    const baseHeight = getCardDimensions(cardWidth, maxWidth).height;
    const neededHeight = getCardHeightForBodyContent(
        maxBodyHeight,
        cardWidth,
        maxWidth,
        options,
    );
    const maxHeight = getMaxCardHeight(cardWidth, maxWidth);
    const finalHeight = Math.min(neededHeight, maxHeight);

    return finalHeight > baseHeight ? finalHeight : null;
};

export const sx = (value, cardWidth) => (value / CARD_SVG.width) * cardWidth;

export const sy = (value, cardHeight) => (value / CARD_SVG.height) * cardHeight;

export const getCardContentLayout = (cardWidth, maxWidth = MAX_CARD_WIDTH, heightOverride = null) => {
    const { width, height } = getCardDimensions(cardWidth, maxWidth, heightOverride);
    const referenceHeight = Math.round(width * (CARD_SVG.height / CARD_SVG.width));
    const isTallCard = height > referenceHeight;
    const headerHeight = referenceHeight;
    const badgeCircleSize = Math.round(sy(BADGE_DISPLAY_CIRCLE_SIZE, headerHeight));
    const badgeScale = badgeCircleSize / BADGE_SVG.circleSize;
    const badgeImgWidth = BADGE_SVG.width * badgeScale;
    const badgeImgHeight = BADGE_SVG.height * badgeScale;
    const textLeft = sx(CARD_BODY.insetLeftX, width);
    const textRight = sx(CARD_SVG.width - CARD_BODY.textMaxRightX, width);

    return {
        width,
        height,
        referenceHeight,
        isTallCard,
        badgeCircleSize,
        badge: {
            imgWidth: badgeImgWidth,
            imgHeight: badgeImgHeight,
            left: sx(CARD_TAB.centerX, width)
                - (BADGE_SVG.circleCenterX / BADGE_SVG.width) * badgeImgWidth,
            top: sy(CARD_TAB.centerY, headerHeight)
                - (BADGE_SVG.circleCenterY / BADGE_SVG.height) * badgeImgHeight,
            goldLeft: sx(CARD_TAB.centerX, width) - badgeCircleSize / 2,
            goldTop: sy(CARD_TAB.centerY, headerHeight) - badgeCircleSize / 2,
        },
        title: {
            top: sy(TITLE_REGION.topY, headerHeight),
            bottom: isTallCard
                ? height - sy(TITLE_REGION.bottomY, headerHeight)
                : sy(CARD_SVG.height - TITLE_REGION.bottomY, height),
            left: textLeft,
            right: textRight,
        },
        body: {
            top: sy(BODY_REGION.topY, headerHeight),
            // Keep bottom inset tied to the SVG reference height so growing
            // tall cards adds body space instead of also growing the pad.
            bottom: sy(CARD_BODY.insetBottomY, referenceHeight),
            left: textLeft,
            right: textRight - sx(50, width),
        },
        bodyTextMaxWidth: width - textLeft - (textRight - sx(50, width)),
        bodyGap: sy(2, headerHeight),
    };
};

export const getInlineCardWidth = (
    containerWidth,
    sectionCount,
    gap = INLINE_CARD_GAP,
    maxWidth = MAX_CARD_WIDTH,
) => {
    const count = Math.max(sectionCount, 1);
    const availableWidth = containerWidth - gap * (count - 1);
    return Math.min(Math.floor(availableWidth / count), maxWidth);
};

export const canFitCardsInline = (
    containerWidth,
    sectionCount,
    gap = INLINE_CARD_GAP,
    maxWidth = MAX_CARD_WIDTH,
) => {
    if (!containerWidth || sectionCount <= 0) {
        return false;
    }

    return getInlineCardWidth(containerWidth, sectionCount, gap, maxWidth) >= MIN_INLINE_CARD_WIDTH;
};

export const getResponsiveCardWidth = (
    containerWidth,
    sectionCount,
    fitsInline,
    {
        forceScroll = false,
        visibleCards = SCROLL_PEEK_VISIBLE_CARDS_MD,
        gap = INLINE_CARD_GAP,
        inlineMaxWidth = MAX_CARD_WIDTH,
        scrollMaxWidth = MOBILE_MAX_CARD_WIDTH,
    } = {},
) => {
    const count = Math.max(sectionCount, 1);

    if (forceScroll) {
        return getScrollCardWidthForPeek(containerWidth, visibleCards, gap, scrollMaxWidth);
    }

    if (fitsInline && canFitCardsInline(containerWidth, count, gap, inlineMaxWidth)) {
        return getInlineCardWidth(containerWidth, count, gap, inlineMaxWidth);
    }

    return getScrollCardWidthForPeek(containerWidth, visibleCards, gap, scrollMaxWidth);
};
